import _get from 'lodash-es/get';

import { objectKeys } from '@iconik-io/core/src/object_entries';
import {
  OBJECT_TYPE_APPLICATIONS,
  OBJECT_TYPE_ASSETS,
  OBJECT_TYPE_AUTOMATIONS,
  OBJECT_TYPE_COLLECTIONS,
  OBJECT_TYPE_CUSTOM_ACTIONS,
  OBJECT_TYPE_EXPORT_LOCATIONS,
  OBJECT_TYPE_FILES,
  OBJECT_TYPE_FORMATS,
  OBJECT_TYPE_GROUPS,
  OBJECT_TYPE_IDENTITY_PROVIDERS,
  OBJECT_TYPE_JOBS,
  OBJECT_TYPE_METADATA_VIEWS,
  OBJECT_TYPE_PERSONS,
  OBJECT_TYPE_SAVED_SEARCH_GROUPS,
  OBJECT_TYPE_SAVED_SEARCHES,
  OBJECT_TYPE_SEGMENTS,
  OBJECT_TYPE_SHARE_USERS,
  OBJECT_TYPE_SHARES,
  OBJECT_TYPE_STORAGES,
  OBJECT_TYPE_SUBCLIPS,
  OBJECT_TYPE_TRANSCODERS,
  OBJECT_TYPE_USERS,
  OBJECT_TYPE_WEBHOOKS,
  OBJECT_TYPE_WIDGETS,
  type ObjectType,
} from '@iconik-io/iconik-api/src/types/object_types';
import type { EntityWithObjectType } from '@iconik-io/shared-lib/src/utils/object_type';

import { GROUP_TYPE_ROLE_GROUP, GROUP_TYPE_TEAM } from '../constants/groups';
import type { AnyObject } from '../types/common';

export const addObjectType = <T = AnyObject, OT = ObjectType>(
  object: T,
  type: OT,
): T & { object_type: OT } => ({
  ...object,
  object_type: type,
});

export const addObjectsType = <T = AnyObject>(
  objects: T[],
  type: ObjectType,
): (T & { object_type: ObjectType })[] =>
  objects.map((object) => addObjectType(object, type));

export const HUMAN_OBJECT_TYPE_SINGULAR: { [type in ObjectType]?: string } = {
  [OBJECT_TYPE_ASSETS]: 'Asset',
  [OBJECT_TYPE_COLLECTIONS]: 'Collection',
  [OBJECT_TYPE_EXPORT_LOCATIONS]: 'Export Location',
  [OBJECT_TYPE_SAVED_SEARCHES]: 'Saved Search',
  [OBJECT_TYPE_SAVED_SEARCH_GROUPS]: 'Saved Search Group',
  [OBJECT_TYPE_FILES]: 'File',
  [OBJECT_TYPE_FORMATS]: 'Format',
  [OBJECT_TYPE_TRANSCODERS]: 'Transcoder',
  [OBJECT_TYPE_STORAGES]: 'Storage',
  [OBJECT_TYPE_GROUPS]: 'Group',
  [OBJECT_TYPE_WEBHOOKS]: 'Webhook',
  [OBJECT_TYPE_APPLICATIONS]: 'Application',
  [OBJECT_TYPE_CUSTOM_ACTIONS]: 'Custom Action',
  [OBJECT_TYPE_JOBS]: 'Job',
  [OBJECT_TYPE_WIDGETS]: 'Widget',
  [OBJECT_TYPE_SHARES]: 'Share',
  [OBJECT_TYPE_SHARE_USERS]: 'Share Participant',
  [OBJECT_TYPE_USERS]: 'User',
  [OBJECT_TYPE_METADATA_VIEWS]: 'Metadata View',
  [OBJECT_TYPE_SEGMENTS]: 'Segment',
  [OBJECT_TYPE_IDENTITY_PROVIDERS]: 'Identity Provider',
  [OBJECT_TYPE_SUBCLIPS]: 'Subclip',
  [OBJECT_TYPE_PERSONS]: 'Person',
  [OBJECT_TYPE_AUTOMATIONS]: 'Automation',
};

export const HUMAN_OBJECT_TYPE_PLURAL: { [type in ObjectType]?: string } = {
  [OBJECT_TYPE_ASSETS]: 'Assets',
  [OBJECT_TYPE_COLLECTIONS]: 'Collections',
  [OBJECT_TYPE_EXPORT_LOCATIONS]: 'Export Locations',
  [OBJECT_TYPE_SAVED_SEARCHES]: 'Saved Searches',
  [OBJECT_TYPE_SAVED_SEARCH_GROUPS]: 'Saved Search Groups',
  [OBJECT_TYPE_FILES]: 'Files',
  [OBJECT_TYPE_FORMATS]: 'Formats',
  [OBJECT_TYPE_TRANSCODERS]: 'Transcoders',
  [OBJECT_TYPE_STORAGES]: 'Storages',
  [OBJECT_TYPE_GROUPS]: 'Groups',
  [OBJECT_TYPE_WEBHOOKS]: 'Webhooks',
  [OBJECT_TYPE_APPLICATIONS]: 'Applications',
  [OBJECT_TYPE_CUSTOM_ACTIONS]: 'Custom actions',
  [OBJECT_TYPE_JOBS]: 'Jobs',
  [OBJECT_TYPE_WIDGETS]: 'Widgets',
  [OBJECT_TYPE_SHARES]: 'Shares',
  [OBJECT_TYPE_SHARE_USERS]: 'Share Participants',
  [OBJECT_TYPE_USERS]: 'Users',
  [OBJECT_TYPE_METADATA_VIEWS]: 'Metadata Views',
  [OBJECT_TYPE_SEGMENTS]: 'Segments',
  [OBJECT_TYPE_IDENTITY_PROVIDERS]: 'Identity Providers',
  [OBJECT_TYPE_SUBCLIPS]: 'Subclips',
  [OBJECT_TYPE_PERSONS]: 'Persons',
  [OBJECT_TYPE_AUTOMATIONS]: 'Automations',
};

const HUMAN_OBJECT_TYPE_TITLE_PATH: { [type in ObjectType]?: string[] } = {
  [OBJECT_TYPE_ASSETS]: ['title'],
  [OBJECT_TYPE_COLLECTIONS]: ['title'],
  [OBJECT_TYPE_SAVED_SEARCHES]: ['name'],
  [OBJECT_TYPE_SAVED_SEARCH_GROUPS]: ['name'],
  [OBJECT_TYPE_FILES]: ['name'],
  [OBJECT_TYPE_FORMATS]: ['name', 'name'],
  [OBJECT_TYPE_TRANSCODERS]: ['name'],
  [OBJECT_TYPE_STORAGES]: ['name'],
  [OBJECT_TYPE_GROUPS]: ['name'],
  [OBJECT_TYPE_WEBHOOKS]: ['name'],
  [OBJECT_TYPE_APPLICATIONS]: ['name'],
  [OBJECT_TYPE_CUSTOM_ACTIONS]: ['title'],
  [OBJECT_TYPE_JOBS]: ['title'],
  [OBJECT_TYPE_WIDGETS]: ['title'],
  [OBJECT_TYPE_SHARE_USERS]: ['email'],
  [OBJECT_TYPE_USERS]: ['first_name'],
  [OBJECT_TYPE_METADATA_VIEWS]: ['name'],
  [OBJECT_TYPE_IDENTITY_PROVIDERS]: ['settings', 'name'],
  [OBJECT_TYPE_SUBCLIPS]: ['title'],
  [OBJECT_TYPE_PERSONS]: ['name'],
};

type TextVariant = 'single' | 'multiple';
type TextVariantsObject = { [variant in TextVariant]: string };
type CallbackType = (params: {
  objectName: string;
  firstItemTitle: string;
  count: number;
}) => TextVariantsObject;

const getSingleObjectType = (firstItem: EntityWithObjectType) => {
  if (
    firstItem.object_type === OBJECT_TYPE_SEGMENTS &&
    firstItem.segment_type === 'COMMENT'
  ) {
    return 'Comment';
  }
  if (
    firstItem.object_type === OBJECT_TYPE_SEGMENTS &&
    (firstItem.segment_type === 'GENERIC' || firstItem.segment_type === 'TAG')
  ) {
    return 'Time-Based Metadata';
  }
  if (firstItem.object_type === OBJECT_TYPE_GROUPS) {
    switch (firstItem.group_type) {
      case GROUP_TYPE_TEAM:
        return 'Team';
      case GROUP_TYPE_ROLE_GROUP:
        return 'Role Group';
    }
  }
  return HUMAN_OBJECT_TYPE_SINGULAR[firstItem.object_type];
};

const getPluralObjectType = (firstItem: EntityWithObjectType) => {
  if (
    firstItem.object_type === OBJECT_TYPE_SEGMENTS &&
    firstItem.segment_type === 'COMMENT'
  ) {
    return 'Comments';
  }
  if (
    firstItem.object_type === OBJECT_TYPE_SEGMENTS &&
    (firstItem.segment_type === 'GENERIC' || firstItem.segment_type === 'TAG')
  ) {
    return 'Time-Based Metadata';
  }
  if (firstItem.object_type === OBJECT_TYPE_GROUPS) {
    switch (firstItem.group_type) {
      case GROUP_TYPE_TEAM:
        return 'Teams';
      case GROUP_TYPE_ROLE_GROUP:
        return 'Role Groups';
    }
  }
  return HUMAN_OBJECT_TYPE_PLURAL[firstItem.object_type];
};
export const formatObjectDefinitionLabel = (
  items: EntityWithObjectType[],
  callback: CallbackType,
): string => {
  const firstItem = items[0] || {};
  const textVariant = items.length === 1 ? 'single' : 'multiple';
  const isMonomorphic = items.every(
    (item) => item.object_type === firstItem.object_type,
  );
  let itemsLabel = isMonomorphic ? getPluralObjectType(firstItem) : 'Items';
  let itemLabel = isMonomorphic ? getSingleObjectType(firstItem) : 'Item';
  itemsLabel = itemsLabel || 'Items';
  itemLabel = itemLabel || 'Item';
  const count = items.length;

  const firstItemTitlePath = HUMAN_OBJECT_TYPE_TITLE_PATH[
    firstItem.object_type
  ] || ['name'];
  return callback({
    objectName: textVariant === 'single' ? itemLabel : itemsLabel,
    firstItemTitle: _get(firstItem, firstItemTitlePath, ''),
    count,
  })[textVariant];
};

export function getChangedFields<T extends object>(oldObj: T, newObj: T) {
  const filteredData: Partial<T> = {};
  for (const key of objectKeys(newObj)) {
    if (newObj[key] !== oldObj[key]) {
      filteredData[key] = newObj[key];
    }
  }
  return filteredData;
}

export function getItemLabelsForObjectType(objectType: ObjectType): {
  singular: string;
  plural: string;
} {
  const singular =
    HUMAN_OBJECT_TYPE_SINGULAR[objectType]?.toLowerCase() ?? objectType;
  const plural =
    HUMAN_OBJECT_TYPE_PLURAL[objectType]?.toLowerCase() ?? objectType;
  return { singular, plural };
}
